import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

const VIEWABLE_RATIO = 0.5;
const VIEWABLE_VIEWPORT_SHARE = 0.5;
// Each step at most doubles the last, so the viewport-share clause below is sampled at
// every element height up to 160 viewports: the observer only calls back at a threshold
// crossing.
const THRESHOLDS = [
  0.003125,
  0.00625,
  0.0125,
  0.025,
  0.05,
  0.1,
  0.2,
  0.4,
  VIEWABLE_RATIO
];

function isViewable(entry: IntersectionObserverEntry) {
  if (!entry.isIntersecting) return false;

  const { height, width } = entry.boundingClientRect;

  if (!height || !width) return false;
  if (entry.intersectionRatio >= VIEWABLE_RATIO) return true;

  const viewportHeight = entry.rootBounds?.height;

  if (!viewportHeight || height <= viewportHeight) return false;
  if (entry.intersectionRect.width / width < VIEWABLE_RATIO) return false;

  return (
    entry.intersectionRect.height / viewportHeight >= VIEWABLE_VIEWPORT_SHARE
  );
}

export default function useImpression<T extends HTMLElement>(
  track: () => void,
  { enabled = true }: { enabled?: boolean } = {}
) {
  const { key: locationKey } = useLocation();
  const elementRef = useRef<T | null>(null);
  const trackedOn = useRef<string>();

  useEffect(() => {
    if (!enabled || trackedOn.current === locationKey) return;

    const element = elementRef.current;

    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (trackedOn.current === locationKey || !entries.some(isViewable))
          return;

        trackedOn.current = locationKey;
        track();
        observer.disconnect();
      },
      { threshold: THRESHOLDS }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [enabled, locationKey, track]);

  return elementRef;
}
